import { useEffect, useState, useRef } from "react";

export const useMultiMonitorDetection = ({
  onMonitorChange = () => {}, //  new callback: fires on BOTH connect + disconnect
  pollingInterval = 2000,
  enableLogging = true,
} = {}) => {
  const [count, setCount] = useState(1);

  const log = (...msg) => enableLogging && console.log("[MONITOR]", ...msg);

  // Track previous count → avoid duplicate events
  const previousCount = useRef(1);

  const browser = (() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("edg/")) return "edge";
    if (ua.includes("chrome") && !ua.includes("edg")) return "chrome";
    if (ua.includes("safari") && !ua.includes("chrome")) return "safari";
    if (ua.includes("firefox")) return "firefox";
    return "unknown";
  })();

  //  unified trigger — fires for both connect & disconnect
  const trigger = (method, newCount) => {
    log("TRIGGER:", method, "count:", newCount);

    if (previousCount.current !== newCount) {
      log("STATE CHANGE:", previousCount.current, "→", newCount);
      previousCount.current = newCount;
      setCount(newCount);

      onMonitorChange({
        method,
        count: newCount,
        connected: newCount > 1, // boolean
      });
    }
  };

  // --------------------------
  // METHOD 1 — Chrome API
  // --------------------------
  const detectScreenDetails = async () => {
    if (!("getScreenDetails" in window)) return false;

    try {
      const details = await window.getScreenDetails();

      // initial count
      trigger("ScreenDetails Initial", details.screens.length);

      //  detects connect/disconnect LIVE
      details.addEventListener("screenschange", () => {
        // log("screenschange EVENT:", details.screens.length);
        trigger("ScreenDetails Event", details.screens.length);
      });

      return true;
    } catch (err) {
      log("ScreenDetails Error:", err);
      return false;
    }
  };

  // --------------------------
  // METHOD 2 — Polling fallback
  // --------------------------
  const fallbackDetect = () => {
    const w = window.screen.width;
    const h = window.screen.height;

    // macbook built-in internal resolutions baseline
    const INTERNAL_MAX_WIDTH = 3024;
    const INTERNAL_MAX_HEIGHT = 1964;

    let detected = 1;
    if (w > INTERNAL_MAX_WIDTH || h > INTERNAL_MAX_HEIGHT) {
      detected = 2; // extended monitor assumed
    }

    trigger("FallbackPoll", detected);
  };

  // Main detect runner
  const runDetect = async () => {
    const usedAPI = await detectScreenDetails();
    if (!usedAPI) fallbackDetect();
  };

  // --------------------------
  // Regular polling (Chrome)
  // --------------------------
  useEffect(() => {
    runDetect(); // initial run

    const id = setInterval(runDetect, pollingInterval);
    return () => clearInterval(id);
  }, []);

  return {
    monitorCount: count,
    browser,
  };
};
